import {
    ActionButton,
    OfficeButton,
    Pie,
    RealTimeWrapper,
    Show,
    TableComponent,
    TableLoader,
} from "components";
import { ChevronDownIcon, ChevronUpIcon } from "@heroicons/react/24/outline";
import { useMatch, useNavigate, useSearch } from "react-location";
import { useUrlState, withPermissions } from "utils";

import AcceptOfferContainer from "../accept-offer";
import { Action } from "components/buttons/action-button";
import AddParticipantsPercentageContainer from "../add-percentage";
import { Colors } from "_graphql/data";
import { Disclosure } from "@headlessui/react";
import { GetOfferPublishedParticipants_getOfferPublishedParticipants } from "_graphql/queries/__generated__/GetOfferPublishedParticipants";
import { LocationGenerics } from "router/location";
import OfferTransactionThreadContainer from "../components/offer-transaction-thread";
import RemoveContainer from "../remove-participants";
import { UserPlus } from "lucide-react";
import ViewSignedPlacingSlipModal from "components/forms/slip-wizard/view-signed-placing-slip-modal";
import config from "config";
import numeral from "numeral";
import { useOffer } from "hooks/data/use-offers";
import { useOfferPublishedParticipants } from "hooks/data/use-publish-offers";
import { useState } from "react";
import { useUpdateKavaThreadBuble } from "hooks/data/use-update-bubble";

export default function OfferParticipation({
    isOfferPublished,
}: {
    isOfferPublished: boolean;
}) {
    const [modal, setModal] = useUrlState("modal");
    const [selectedInterest, setSelectedInterest] = useState(null);
    const [signedSlipTarget, setSignedSlipTarget] = useState<{
        reinsurerId: string;
        companyName?: string;
    } | null>(null);
    const searchParams = useSearch<LocationGenerics>();
    const { params } = useMatch<LocationGenerics>();
    const navigate = useNavigate<LocationGenerics>();
    const { loading, offer, refetch } = useOffer({
        offer_id: params.offer || "",
    });

    const {
        participants: publishedParticipants,
        loading: participantsLoading,
        refetch: refetchParticipants,
    } = useOfferPublishedParticipants({
        offerId: params.offer || "",
    });
    const { updateBubble } = useUpdateKavaThreadBuble();

    const dispatchAction =
        (
            id: string,
            action: Exclude<Action, "expand" | "goto" | "clone">,
            participation_id?: string,
        ) =>
        () => {
            navigate({
                search: (old) => ({
                    ...old,
                    id,
                    reinsurer: participation_id,
                    modal: action,
                }),
            });
        };

    const handleAcceptOffer = (
        row: GetOfferPublishedParticipants_getOfferPublishedParticipants,
    ) => {
        setSelectedInterest(row as any);
        setModal("acceptOffer");
    };

    const updateThreadBubble = (messageIds: string[]) => {
        if (!messageIds || !messageIds.length) return;

        updateBubble({
            variables: {
                input: messageIds,
            },
        })
            .then(() => {})
            .catch((err) => {});
    };

    const participants =
        offer?.offer_participant?.map((item, idx) => ({
            bgColor: "bg-[" + Colors[idx] + "]",
            color: Colors[idx],
            id: item?.reinsurer?.re_company_name || "",
            label: item?.reinsurer?.re_company_name || "",
            value: item?.offer_participant_percentage || 0,
        })) || [];

    const sharedValue =
        offer?.offer_participant
            ?.reduce(
                (acc, curr) => acc + (curr?.offer_participant_percentage || 0),
                0,
            )
            .toFixed(4) || 0;

    return (
        <div className="flex flex-col flex-1 overflow-y-auto p-4 sm:p-6 gap-6">
            {/* Reinsurers List Section */}
            <div className="bg-white dark:bg-gray-800 shadow rounded-lg">
                <Disclosure as="div" defaultOpen={true}>
                    {({ open }) => (
                        <>
                            <Disclosure.Button
                                as="div"
                                className="flex justify-between items-center cursor-pointer w-full px-4 py-3">
                                <div>
                                    <h3 className="text-md leading-6 font-normal text-gray-900 dark:text-gray-100">
                                        Reinsurers List
                                    </h3>
                                    <p className="mt-1 text-xs text-gray-500">
                                        List of reinsurers who have been added
                                        to the offer
                                    </p>
                                </div>
                                <div>
                                    {open ? (
                                        <ChevronUpIcon className="h-6 w-6 text-gray-400 dark:text-gray-500" />
                                    ) : (
                                        <ChevronDownIcon className="h-6 w-6 text-gray-400 dark:text-gray-500" />
                                    )}
                                </div>
                            </Disclosure.Button>
                            <Disclosure.Panel
                                as="div"
                                className="px-4 pb-4 pt-2 border-t border-gray-200 dark:border-gray-700">
                                <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
                                    <div className="md:col-span-3">
                                        <RealTimeWrapper
                                            channel={
                                                config.pusher.channels.kava
                                            }
                                            callBack={refetch}
                                            events={[
                                                "reinsurer.remove_interest",
                                                "reinsurer.thread_notification",
                                                "broker.remove_reinsurer_interest",
                                                "broker.accepted_interest",
                                            ]}>
                                            <TableComponent
                                                title={"participants"}
                                                hasSearch
                                                renderActions={() => (
                                                    <div className="flex space-x-1">
                                                        <OfficeButton
                                                            size="sm"
                                                            onClick={() =>
                                                                setModal(
                                                                    "create",
                                                                )
                                                            }>
                                                            <UserPlus className="w-5 h-5 mr-2" />
                                                            <span className="inline text-xs sm:hidden">
                                                                Add
                                                            </span>
                                                            <span className="hidden sm:inline">
                                                                Add Participant
                                                            </span>
                                                        </OfficeButton>
                                                    </div>
                                                )}
                                                loading={loading}
                                                columns={[
                                                    {
                                                        accessor:
                                                            "reinsurer.re_company_name",
                                                        name: "Company Name",
                                                    },
                                                    {
                                                        accessor:
                                                            "reinsurer.re_company_email",
                                                        name: "Company Email",
                                                    },
                                                    {
                                                        accessor:
                                                            "offer_participant_percentage",
                                                        name: "Participant %",
                                                    },
                                                    {
                                                        accessor: "",
                                                        name: "Actions",
                                                        render: (row) => (
                                                            <div className="flex space-x-1">
                                                                {withPermissions(
                                                                    [
                                                                        "*:*",
                                                                        "offers:*",
                                                                        "offers:manage",
                                                                        "endorsements:*",
                                                                        "endorsements:manage",
                                                                    ],
                                                                )(
                                                                    <ActionButton
                                                                        action={
                                                                            "assignPercentage"
                                                                        }
                                                                        tooltip={
                                                                            row?.offer_participant_percentage
                                                                                ? "Update Percentage"
                                                                                : "Add Percentage"
                                                                        }
                                                                        onClick={dispatchAction(
                                                                            row?.offer_participant_id ||
                                                                                "",
                                                                            "assignPercentage",
                                                                            numeral(
                                                                                offer?.facultative_offer,
                                                                            )
                                                                                .subtract(
                                                                                    sharedValue,
                                                                                )
                                                                                .add(
                                                                                    row?.offer_participant_percentage,
                                                                                )
                                                                                .format(
                                                                                    "#,#.####",
                                                                                ),
                                                                        )}
                                                                    />,
                                                                )}
                                                                {withPermissions(
                                                                    [
                                                                        "*:*",
                                                                        "offers:manage",
                                                                        "offers:delete",
                                                                        "endorsements:manage",
                                                                        "endorsements:delete",
                                                                    ],
                                                                )(
                                                                    <ActionButton
                                                                        action={
                                                                            "remove"
                                                                        }
                                                                        tooltip={
                                                                            "Remove participant"
                                                                        }
                                                                        onClick={dispatchAction(
                                                                            row?.offer_participant_id ||
                                                                                "",
                                                                            "remove",
                                                                            row
                                                                                ?.reinsurer
                                                                                ?.reinsurer_id ||
                                                                                "",
                                                                        )}
                                                                    />,
                                                                )}
                                                                {/* <Show if={Boolean(row?.offer_published_participant?.offer_published_participant_id)}>
                                  <ActionButton
                                    action={"chat"}
                                    count={row.thread_notification?.length || 0}
                                    tooltip={"Transaction Thread"}
                                    callBack={() => {
                                      updateThreadBubble(row?.thread_notification?.map(el => el?.id || "") || []);
                                    }}
                                    onClick={() => {
                                      navigate({
                                        search: (old) => ({
                                          ...old,
                                          id: row.offer_published_participant?.offer_published_participant_id,
                                          modal: "chat",
                                        }),
                                      });
                                    }}
                                  />
                                </Show> */}
                                                            </div>
                                                        ),
                                                    },
                                                ]}
                                                data={
                                                    offer?.offer_participant ||
                                                    []
                                                }
                                                renderLoader={() => (
                                                    <TableLoader
                                                        items={[1, 1, 1, 1]}
                                                    />
                                                )}
                                                refetch={refetch}
                                            />
                                        </RealTimeWrapper>
                                    </div>
                                    <div className="flex flex-col items-center justify-between py-4">
                                        <span className="text-center text-gray-600 dark:text-gray-300 text-sm font-bold mb-4">
                                            Percentage Left:{" "}
                                            {numeral(offer?.facultative_offer)
                                                .subtract(sharedValue)
                                                .format("0.0000")}{" "}
                                            %
                                        </span>
                                        <div className="w-full max-w-[240px] h-60">
                                            <Pie
                                                data={[
                                                    ...participants,
                                                    {
                                                        label: "Unassigned",
                                                        value: Math.max(
                                                            0,
                                                            (offer?.facultative_offer ||
                                                                0) -
                                                                participants.reduce(
                                                                    (
                                                                        acc,
                                                                        curr,
                                                                    ) =>
                                                                        acc +
                                                                        curr.value,
                                                                    0,
                                                                ),
                                                        ),
                                                        color: "#ccc",
                                                        bgColor: "bg-[#ccc]",
                                                        id: "Unassigned",
                                                    },
                                                ]}
                                                innerRadius={0.2}
                                            />
                                        </div>
                                    </div>
                                </div>
                            </Disclosure.Panel>
                        </>
                    )}
                </Disclosure>
            </div>

            {/* Reinsurers Interest Section */}
            <Show if={isOfferPublished}>
                <RealTimeWrapper
                    channel={config.pusher.channels.kava}
                    events={[
                        "reinsurer.interest",
                        "reinsurer.remove_interest",
                        "reinsurer.thread_notification",
                        "broker.remove_reinsurer_interest",
                        "broker.accepted_interest",
                    ]}
                    callBack={refetchParticipants}>
                    <div className="bg-white dark:bg-gray-800 shadow rounded-lg">
                        <Disclosure as="div" defaultOpen={true}>
                            {({ open }) => (
                                <>
                                    <Disclosure.Button
                                        as="div"
                                        className="flex justify-between items-center cursor-pointer w-full px-4 py-3">
                                        <div>
                                            <h3 className="text-md leading-6 font-normal text-gray-900 dark:text-gray-100">
                                                Reinsurers Interest
                                            </h3>
                                            <p className="mt-1 text-xs text-gray-500">
                                                List of reinsurers who have
                                                expressed interest in the offer
                                            </p>
                                        </div>
                                        <div>
                                            {open ? (
                                                <ChevronUpIcon className="h-6 w-6 text-gray-400 dark:text-gray-500" />
                                            ) : (
                                                <ChevronDownIcon className="h-6 w-6 text-gray-400 dark:text-gray-500" />
                                            )}
                                        </div>
                                    </Disclosure.Button>
                                    <Disclosure.Panel
                                        as="div"
                                        className="px-4 pb-4 pt-2 border-t border-gray-200 dark:border-gray-700">
                                        <TableComponent
                                            title={"interest"}
                                            hasSearch
                                            loading={participantsLoading}
                                            columns={[
                                                {
                                                    accessor:
                                                        "reinsurer.re_company_name",
                                                    name: "Company Name",
                                                },
                                                {
                                                    accessor:
                                                        "reinsurer.re_company_email",
                                                    name: "Company Email",
                                                },
                                                {
                                                    accessor:
                                                        "offer_participant_percentage",
                                                    name: "Participation %",
                                                },
                                                {
                                                    accessor:
                                                        "reinsurer_representative.rep_first_name",
                                                    name: "Added By",
                                                    render: (
                                                        row: GetOfferPublishedParticipants_getOfferPublishedParticipants,
                                                    ) => (
                                                        <span>
                                                            {row
                                                                .reinsurer_representative
                                                                ?.rep_first_name ||
                                                                "N/A"}{" "}
                                                        </span>
                                                    ),
                                                },
                                                {
                                                    accessor: "status",
                                                    name: "Status",
                                                    render: (
                                                        row: GetOfferPublishedParticipants_getOfferPublishedParticipants,
                                                    ) => (
                                                        <span
                                                            className={`inline-block px-2 py-1 rounded text-xs font-medium ${
                                                                row.participation_status ===
                                                                "ACCEPTED"
                                                                    ? "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-100"
                                                                    : "bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-100"
                                                            }`}>
                                                            {
                                                                row.participation_status
                                                            }
                                                        </span>
                                                    ),
                                                },
                                                {
                                                    accessor: "",
                                                    name: "Actions",
                                                    render: (row) => (
                                                        <div className="flex space-x-1">
                                                            <ActionButton
                                                                action={"view"}
                                                                tooltip="View Signed Slip"
                                                                onClick={() =>
                                                                    setSignedSlipTarget(
                                                                        {
                                                                            reinsurerId:
                                                                                row
                                                                                    .reinsurer
                                                                                    ?.reinsurer_id ||
                                                                                "",
                                                                            companyName:
                                                                                row
                                                                                    .reinsurer
                                                                                    ?.re_company_name,
                                                                        },
                                                                    )
                                                                }
                                                            />
                                                            {withPermissions([
                                                                "*:*",
                                                                "offers:*",
                                                                "offers:manage",
                                                                "offers:update",
                                                            ])(
                                                                <ActionButton
                                                                    action={
                                                                        "accept"
                                                                    }
                                                                    tooltip={
                                                                        Number(
                                                                            row?.offer_participant_percentage,
                                                                        ) <= 0
                                                                            ? "You Cannot accept zero percentage"
                                                                            : "Accept Participation"
                                                                    }
                                                                    onClick={() =>
                                                                        handleAcceptOffer(
                                                                            row,
                                                                        )
                                                                    }
                                                                    disabled={Boolean(
                                                                        row.participation_status ===
                                                                            "ACCEPTED" ||
                                                                            Number(
                                                                                row?.offer_participant_percentage,
                                                                            ) <=
                                                                                0,
                                                                    )}
                                                                />,
                                                            )}
                                                            <ActionButton
                                                                action={"chat"}
                                                                count={
                                                                    row
                                                                        .unread_messages
                                                                        ?.length ||
                                                                    0
                                                                }
                                                                tooltip={
                                                                    "Transaction Thread"
                                                                }
                                                                callBack={() => {
                                                                    updateThreadBubble(
                                                                        row?.unread_messages?.map(
                                                                            (
                                                                                message,
                                                                            ) =>
                                                                                message?.id ||
                                                                                "",
                                                                        ) || [],
                                                                    );
                                                                }}
                                                                onClick={() => {
                                                                    navigate({
                                                                        search: (
                                                                            old,
                                                                        ) => ({
                                                                            ...old,
                                                                            id: row.offer_published_participant_id,
                                                                            modal: "chat",
                                                                            reinsurer:
                                                                                row
                                                                                    .reinsurer
                                                                                    ?.re_company_name,
                                                                            offer:
                                                                                offer?.place_it_id ||
                                                                                "",
                                                                        }),
                                                                    });
                                                                }}
                                                            />
                                                        </div>
                                                    ),
                                                },
                                            ]}
                                            data={publishedParticipants}
                                            renderLoader={() => (
                                                <TableLoader
                                                    items={[1, 1, 1, 1, 1]}
                                                />
                                            )}
                                            refetch={refetchParticipants}
                                        />
                                    </Disclosure.Panel>
                                </>
                            )}
                        </Disclosure>
                    </div>
                </RealTimeWrapper>
            </Show>

            {/* Modals */}
            {searchParams?.id && (
                <>
                    <AddParticipantsPercentageContainer
                        open={modal === "assignPercentage"}
                        setOpen={(val: boolean) =>
                            navigate({
                                search(prev) {
                                    return {
                                        ...prev,
                                        modal: val
                                            ? "assignPercentage"
                                            : undefined,
                                        id: val ? searchParams.id : undefined,
                                        reinsurer: val
                                            ? searchParams.reinsurer
                                            : undefined,
                                    };
                                },
                            })
                        }
                    />
                    <RemoveContainer
                        open={modal === "remove"}
                        setOpen={(val: boolean) =>
                            setModal(val ? "remove" : undefined)
                        }
                    />
                </>
            )}

            <AcceptOfferContainer
                open={modal === "acceptOffer"}
                setOpen={(val: boolean) =>
                    setModal(val ? "acceptOffer" : undefined)
                }
                reinsurerData={selectedInterest}
                refetch={refetch}
            />

            {searchParams?.id && modal === "chat" && (
                <OfferTransactionThreadContainer
                    open={modal === "chat"}
                    setOpen={(val: boolean) =>
                        setModal(val ? "chat" : undefined)
                    }
                    refetch={refetchParticipants}
                />
            )}

            {signedSlipTarget && (
                <ViewSignedPlacingSlipModal
                    open={Boolean(signedSlipTarget)}
                    setOpen={(val: boolean) => {
                        if (!val) {
                            setSignedSlipTarget(null);
                        }
                    }}
                    offerId={params.offer || ""}
                    reinsurerId={signedSlipTarget.reinsurerId}
                    companyName={signedSlipTarget.companyName}
                />
            )}
        </div>
    );
}
